import type { CardContentTypeT, CardSizeT, CardTypeT } from 'blocks/Cards'

import { useMemo } from 'react'
import { CardContentType, CardSize, CardType } from 'blocks/Cards'

type SimpleVisibleCardsProps = {
  clientWidth: number
  contentType: CardContentTypeT
  type: CardTypeT
  size: CardSizeT
}

export const CardDimensions = {
  MOBILE_S: 375,
  MOBILE_L: 600,
  TABLET_S: 768,
  TABLET_L: 1240,
  DESKTOP_S: 1440,
  DESKTOP_M: 1640,
  DESKTOP_L: 1920,
} as const
export type CardDimensions =
  (typeof CardDimensions)[keyof typeof CardDimensions]
export const CardDimensionsIndexInArray = {
  MOBILE_S: 0, // 375
  MOBILE_L: 1, // 600
  TABLET_S: 2, // 768
  TABLET_L: 3, // 1240
  DESKTOP_S: 4, // 1440
  DESKTOP_M: 5, // 1640
  DESKTOP_L: 6, // 1920
} as const
export type CardDimensionsIndexInArray =
  (typeof CardDimensionsIndexInArray)[keyof typeof CardDimensionsIndexInArray]

// card counts according to https://setplex.notion.site/Adaptive-resize-rules-b0871792a7f842e4baa819ca13bd487d
const RowVisibleCards = {
  [CardContentType.VODS_ADAPTIVE]: {
    [CardType.VERTICAL]: {
      [CardSize.L]: [2, 3, 3, 4, 4, 5, 5],
      [CardSize.M]: [3, 4, 4, 5, 5, 6, 6],
      [CardSize.S]: [3, 4, 5, 6, 6, 7, 7],
    },
    [CardType.HORIZONTAL]: {
      [CardSize.L]: [1, 2, 2, 2, 2, 3, 3],
      [CardSize.M]: [1, 2, 2, 3, 3, 4, 4],
      [CardSize.S]: [2, 2, 3, 4, 4, 5, 5],
    },
    [CardType.VERTICAL_VOD_TOP_CONTENT]: {
      [CardSize.M]: [1.32, 2.4, 2.32, 3, 3, 3.57, 3.54],
    },
    [CardType.HORIZONTAL_VOD_TOP_CONTENT]: {
      [CardSize.M]: [1.04, 1.58, 2.04, 2.32, 2.32, 3.1, 3.1],
    },
    // FP-4279: 6/5/4/3/2/1 columns as width drops through 1920/1440/1240/[TBD]/600/0.
    // The ticket leaves both cutoffs below 1240 as "TBD"; both reuse TABLET_S
    // (768), the only stop this ladder has between 600 and 1240 — no new tier
    // is added.
    //
    // 1240-1439 follows the ticket's "4", not the search canvas (Figma
    // `15617:152805`): its `1439-1240` frame actually renders 5 columns, the
    // same reading FP-4278 recorded. Ticket and design disagree here; per
    // product, the ticket text wins.
    //
    // 1640-1919 is 6, not 5: QA (FP-4279) flagged the 1920-1640 band showing
    // 5 instead of 6 against the design frame at Figma `16554:167836`, which
    // renders 6 columns for that whole band. FP-4296 hides Show More from
    // 1240px up, so this band shows every item without a toggle.
    [CardType.HORIZONTAL_SEARCH]: {
      [CardSize.M]: [1, 2, 3, 4, 5, 6, 6],
    },
  },
  [CardContentType.EVENTS_ADAPTIVE]: {
    [CardType.VERTICAL_EVENT_TOP_CONTENT]: {
      [CardSize.M]: [1.32, 2.4, 2.32, 3, 3, 3.57, 3.54],
    },
    [CardType.HORIZONTAL_EVENT_TOP_CONTENT]: {
      [CardSize.M]: [1.04, 1.58, 2.04, 2.32, 2.32, 3.1, 3.1],
    },
  },
  [CardContentType.CHANNELS_ADAPTIVE]: {
    [CardType.HORIZONTAL]: {
      [CardSize.L]: [1, 2, 3, 4, 4, 5, 5],
      [CardSize.M]: [1, 3, 3, 5, 5, 6, 6],
      [CardSize.S]: [2, 4, 4, 6, 6, 7, 7],
    },
    [CardType.HORIZONTAL_CHANNEL_TOP_CONTENT]: {
      [CardSize.M]: [1.23, 2.25, 3, 3.78, 3.78, 4.54, 4.48],
    },
  },
  [CardContentType.EPISODES_ADAPTIVE]: {
    [CardType.HORIZONTAL]: {
      [CardSize.S]: [2, 3, 3, 5, 5, 6, 7],
      // FP-4261 / FP-4324: series details carousel.
      // 375–1440 keep a 0.25 peek (Figma 16273:216762 / 16273:212315).
      // At 1640+ the 1920 frame (16031:194104) draws five whole 342.4px cards
      // with symmetric 80px padding; the 348×195.75 layer is the scroll-button
      // overlay dummy, not a live card. 5.25 made those cards ~325px.
      [CardSize.M]: [1.25, 2.25, 3.25, 4.25, 4.25, 5, 5],
    },
  },
}

export const useSimpleVisibleCards = ({
  clientWidth,
  contentType,
  type,
  size,
}: SimpleVisibleCardsProps): number => {
  return useMemo(() => {
    if (!clientWidth || !contentType || !type || !size) return 0

    const cards = RowVisibleCards[contentType][type][size]

    if (clientWidth < CardDimensions.MOBILE_L)
      return cards[CardDimensionsIndexInArray.MOBILE_S]

    if (clientWidth < CardDimensions.TABLET_S)
      return cards[CardDimensionsIndexInArray.MOBILE_L]

    if (clientWidth < CardDimensions.TABLET_L)
      return cards[CardDimensionsIndexInArray.TABLET_S]

    if (clientWidth < CardDimensions.DESKTOP_S)
      return cards[CardDimensionsIndexInArray.TABLET_L]

    if (clientWidth < CardDimensions.DESKTOP_M)
      return cards[CardDimensionsIndexInArray.DESKTOP_S]

    if (clientWidth < CardDimensions.DESKTOP_L)
      return cards[CardDimensionsIndexInArray.DESKTOP_M]

    return cards[CardDimensionsIndexInArray.DESKTOP_L]
  }, [clientWidth, contentType, type, size])
}
